/**
 * 계정별 노출 영역 — 본사가 "이 계정에게 어떤 영역을 보여줄지"를 한자리에서 정하는 규칙.
 *
 * ## 새 장치를 만들지 않는다
 * 계정이 무엇을 보는지는 이미 두 장치가 정한다. 이 파일은 그 둘을 **"영역" 하나의 말로 번역**할 뿐이다.
 *   ① 개인별 권한 예외(user_permission_overrides) — 개통 CRM·정책관리·재고관리·카톡 상담 묶음을
 *      사람 단위로 켜고 끈다(shared/rbac.ts). 저장 단위가 **사람**이다.
 *   ② 이용 범위(tenants.edition) — "선택 이용" 매장은 평소 메뉴 대신 켜 준 기능(신분증 스캔 등)만
 *      본다(shared/external-edition.ts). 저장 단위가 **매장**이다.
 *
 * ## "신분증 스캔"은 두 얼굴이다 (2026-09-30 개정)
 *   · **스캔만** 고르면 → "스캔 전용" 모드. 매장 이용 범위를 "선택 이용"으로 두고, 메뉴를 통째로
 *     신분증 스캔 하나로 갈아끼운다(스캐너 캠페인 초기 회원들이 이 모양이었다).
 *   · **다른 영역과 함께** 고르면 → 전체 이용 매장의 **개통 CRM 메뉴 안의 한 줄**이 된다. 스캔은
 *     고객을 등록하는 입구라 CRM 과 떨어져 쓸 일이 없다. 그래서 전체 이용에서는 **CRM 이 보이면
 *     스캔도 보인다**(따로 저장하는 값이 없다 — 저장 칸이 따로 있으면 둘이 어긋난다).
 *   예전엔 전체 이용 메뉴에 스캔 자리가 아예 없어서 둘을 같이 줄 수 없었다. 스캐너 캠페인 고객에게
 *   "정책 빼고 전부 + 스캔"을 열어 주기로 하면서(사용자 결정) CRM 메뉴에 스캔을 올렸다.
 *
 * ## ⚠️ 이용 범위는 매장 단위다
 * 한 계정을 스캔 전용으로 바꾸면 **같은 매장의 다른 계정도 전부** 스캔 전용이 된다.
 * 그래서 이용 범위가 바뀌는 적용은 서버가 한 번 멈추고(409) 영향받는 매장·계정을 돌려준다 —
 * 화면이 그걸 보여주고 확인을 받은 뒤에만 다시 보낸다.
 *
 * 서버(판정·적용)와 관리자 콘솔(표시·필터)이 함께 쓴다.
 */

import {
  DEFAULT_MATRIX,
  canAccess,
  deriveDisplayLevel,
  isGlobalAdminScope,
  resolvePermLevel,
  type PermLevelCode,
  type PermMatrix,
  type PermOverrideMap,
} from "./rbac.js";

/** 사람 단위(권한 예외)로 켜고 끄는 메뉴 영역. */
export const MENU_AREA_KEYS = ["crm", "policy", "inventory", "kakao_counsel"] as const;
export type MenuAreaKey = (typeof MENU_AREA_KEYS)[number];

/** 스캔 전용 모드 — 매장 이용 범위를 "선택 이용"으로 둔다. 선택 이용 카탈로그 키와 같다. */
export const SCAN_AREA_KEY = "id-card-scan" as const;

export type AreaKey = MenuAreaKey | typeof SCAN_AREA_KEY;
export const AREA_KEYS: readonly AreaKey[] = [...MENU_AREA_KEYS, SCAN_AREA_KEY];

export const AREA_LABEL: Record<AreaKey, string> = {
  crm: "개통 CRM",
  policy: "정책관리",
  inventory: "재고관리",
  kakao_counsel: "카톡 상담",
  [SCAN_AREA_KEY]: "신분증 스캔",
};

export function isAreaKey(v: unknown): v is AreaKey {
  return typeof v === "string" && (AREA_KEYS as readonly string[]).includes(v);
}

/** 이 계정의 영역을 이 화면에서 바꿀 수 없는 이유. null 이면 바꿀 수 있다. */
export type NotEditableReason = "global_admin" | "lead" | "no_store" | null;

export const NOT_EDITABLE_LABEL: Record<Exclude<NotEditableReason, null>, string> = {
  global_admin: "본사 관리자는 항상 전체를 봅니다",
  lead: "리드(체험) 계정 — 리드 계정 화면에서 정식 승격 후 설정",
  no_store: "소속 매장이 없는 계정",
};

export interface AccountAreaInput {
  role: string;
  tenantId: string | null;
  /** 이미 resolveEdition 을 거친 값 */
  edition: "full" | "partial";
  overrides: PermOverrideMap;
  /** 선택 이용 기능 중 켜져 있는 키(전역 스위치) */
  enabledFeatureKeys: readonly string[];
  matrix?: PermMatrix;
}

export function notEditableReason(role: string, tenantId: string | null): NotEditableReason {
  if (isGlobalAdminScope(role, tenantId)) return "global_admin";
  if (role === "lead") return "lead";
  if (!tenantId) return "no_store";
  return null;
}

/**
 * 이 계정이 지금 실제로 보는 영역. 사이드바(src/layout/AppSidebar.tsx)의 판정 순서를 그대로 따른다 —
 * 선택 이용 → 리드 → 권한.
 */
export function visibleAreas(a: AccountAreaInput): AreaKey[] {
  const matrix = a.matrix ?? DEFAULT_MATRIX;
  if (isGlobalAdminScope(a.role, a.tenantId)) return [...MENU_AREA_KEYS];
  if (a.edition === "partial") {
    return a.enabledFeatureKeys.includes(SCAN_AREA_KEY) ? [SCAN_AREA_KEY] : [];
  }
  // 리드는 정해진 정책 시연 화면만 본다(src/lib/leadDemo.ts).
  if (a.role === "lead") return ["policy"];
  const hasTenant = a.tenantId != null;
  const menus: AreaKey[] = MENU_AREA_KEYS.filter((g) =>
    canAccess(resolvePermLevel(a.role, hasTenant, g, matrix, a.overrides)),
  );
  // 전체 이용에서는 스캔이 CRM 메뉴의 한 줄이다(머리말). 전역 스위치가 꺼져 있으면 안 보인다.
  if (scanInFullMenu(menus, a.enabledFeatureKeys)) menus.push(SCAN_AREA_KEY);
  return menus;
}

/**
 * 전체 이용 매장의 개통 CRM 메뉴에 신분증 스캔을 올릴지. 사이드바(src/layout/AppSidebar.tsx)와
 * 위 visibleAreas 가 같은 판정을 쓴다 — 메뉴와 «보이는 영역» 표시가 어긋나지 않게.
 */
export function scanInFullMenu(
  visibleMenuAreas: readonly string[],
  enabledFeatureKeys: readonly string[],
): boolean {
  return visibleMenuAreas.includes("crm") && enabledFeatureKeys.includes(SCAN_AREA_KEY);
}

/**
 * 원하는 영역 조합이 성립하는지. 스캔을 다른 영역과 함께 주려면 개통 CRM 이 있어야 한다 —
 * 전체 이용에서 스캔은 CRM 메뉴의 한 줄이라서다(머리말 참조).
 */
export function validateAreaSelection(areas: readonly AreaKey[]): string | null {
  if (areas.includes(SCAN_AREA_KEY) && areas.length > 1 && !areas.includes("crm")) {
    return "신분증 스캔을 다른 영역과 함께 쓰려면 개통 CRM 도 켜야 합니다(스캔은 CRM 메뉴 안에 들어갑니다)";
  }
  return null;
}

/** 스캔만 골랐는가 = "스캔 전용"(선택 이용) 모드로 가는가. */
export function isScanOnly(areas: readonly AreaKey[]): boolean {
  return areas.length === 1 && areas[0] === SCAN_AREA_KEY;
}

/** 메뉴 영역 하나의 개인 예외를 어떻게 바꿀지 — set = 이 값으로 저장, delete = 역할 기본값으로 복귀. */
export type OverrideChange = { group: MenuAreaKey; op: "set"; level: PermLevelCode } | { group: MenuAreaKey; op: "delete" };

export interface AccountAreaPlan {
  /** 이용 범위를 바꿔야 하면 목표값, 그대로면 null */
  editionTo: "full" | "partial" | null;
  /** 메뉴 영역 예외 변경. 스캔 전용으로 갈 때는 비운다(선택 이용이면 예외가 화면에 안 쓰인다). */
  overrideChanges: OverrideChange[];
}

/**
 * 한 계정을 원하는 영역으로 맞추는 데 필요한 변경을 계산한다(순수 함수 — 저장은 호출자가).
 *
 * 켤 때 규칙: 역할 기본값으로 이미 보이면 예외를 지워 기본값에 맡긴다(권한을 괜히 올리지 않는다).
 * 기존 예외가 읽기/쓰기면 그대로 둔다(본사가 일부러 준 읽기 제한을 풀지 않는다).
 * 기본값이 "없음"이면 **쓰기**로 연다 — 운영 권한표에서 매장직원은 CRM·재고·카톡 상담 기본값이
 * "없음"이라, 읽기로만 열면 메뉴는 보이는데 고객 처리를 못 하는 반쪽 상태가 된다(검증에서 드러났다:
 * 쓰기였던 직원을 껐다 켜니 읽기로 떨어졌다). 매장에 이 영역을 줄 때 쓰는 값(시드·운영)도 쓰기다.
 * 끌 때 규칙: 예외를 "없음"으로 저장한다.
 */
export function planAccountAreas(
  a: AccountAreaInput,
  desired: readonly AreaKey[],
): AccountAreaPlan {
  const matrix = a.matrix ?? DEFAULT_MATRIX;
  if (isScanOnly(desired)) {
    return { editionTo: a.edition === "partial" ? null : "partial", overrideChanges: [] };
  }
  // 스캔 + 다른 영역 = 전체 이용. 스캔은 CRM 을 따라오므로 아래에서 따로 다루지 않는다.
  const editionTo = a.edition === "partial" ? "full" : null;
  const hasTenant = a.tenantId != null;
  const displayLevel = deriveDisplayLevel(a.role, hasTenant);
  const overrideChanges: OverrideChange[] = [];
  for (const group of MENU_AREA_KEYS) {
    const current = a.overrides[group];
    const roleDefault = matrix[displayLevel]?.[group] ?? DEFAULT_MATRIX[displayLevel][group];
    if (desired.includes(group)) {
      if (current && canAccess(current)) continue;
      if (canAccess(roleDefault)) {
        if (current) overrideChanges.push({ group, op: "delete" });
      } else {
        overrideChanges.push({ group, op: "set", level: "write" });
      }
    } else if (canAccess(current ?? roleDefault)) {
      overrideChanges.push({ group, op: "set", level: "none" });
    }
  }
  return { editionTo, overrideChanges };
}
